import { type FC, Suspense } from 'react';
import {
    graphql,
    useFragment,
    useRelayEnvironment,
    useLazyLoadQuery,
    usePaginationFragment,
} from 'react-relay';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import { flattenConnectionNodes } from 'dibs-ts-utils/exports/flattenConnectionNodes.ts';
import ItemTilesCarousel from 'dibs-product-tile-carousel/exports/ItemTilesCarousel.ts';
import authModalLoader from 'dibs-buyer-layout/exports/authModalLoader.ts';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency.tsx';

import SharedFavoritesProvider from '../../sharedComponents/SharedFavoritesProvider.jsx';
import { SharedHeart } from '../../sharedComponents/SharedHeart.tsx';
import { useHomePageContext } from '../HomePageContext.tsx';
import { VIDEO_MODULE_TRIGGER } from './videoComponentTrackingHelpers.ts';

import { type HpSharedVideoComponentCarouselQuery as HpSharedVideoComponentCarouselQueryType } from './__generated__/HpSharedVideoComponentCarouselQuery.graphql.ts';
import { type HpSharedVideoComponentCarousel_viewer$key } from './__generated__/HpSharedVideoComponentCarousel_viewer.graphql.ts';
import { type HpSharedVideoComponentCarousel_user$key } from './__generated__/HpSharedVideoComponentCarousel_user.graphql.ts';

const CAROUSEL_ITEMS_TO_SHOW_RESP = 4;
const CAROUSEL_ITEMS_TO_SHOW_MOBILE = 1.5;
const CAROUSEL_CAPACITY = 12;

const carouselItemsQuery = graphql`
    query HpSharedVideoComponentCarouselQuery(
        $uriRef: String!
        $userCountryCode: String
        $priceBookName: String
        $isTrade: Boolean
        $userId: String
    ) {
        viewer {
            ...HpSharedVideoComponentCarousel_viewer @arguments(uriRef: $uriRef)
        }
    }
`;

const viewerFragment = graphql`
    fragment HpSharedVideoComponentCarousel_viewer on Viewer
    @refetchable(queryName: "HpSharedVideoComponentCarouselPaginationQuery")
    @argumentDefinitions(
        count: { type: "Int", defaultValue: 5 }
        cursor: { type: "String" }
        uriRef: { type: "String!" }
    ) {
        itemSearchItems(
            first: $count
            after: $cursor
            uriRef: $uriRef
            userCountryCode: $userCountryCode
            priceBookName: $priceBookName
        ) @connection(key: "HpSharedVideoComponentCarousel_itemSearchItems", filters: []) {
            # eslint-disable-next-line relay/unused-fields
            edges {
                node {
                    serviceId
                    ...ItemTilesCarousel_items
                        @arguments(
                            fetchFavorites: false
                            userId: $userId
                            isTrade: $isTrade
                            priceBookName: $priceBookName
                        )
                    ...SharedHeart_item
                }
            }
            totalResults
        }
        ...ItemTilesCarousel_viewer @arguments(fetchFavorites: false, userId: $userId)
        ...SharedFavoritesProvider_viewer
    }
`;

const userFragment = graphql`
    fragment HpSharedVideoComponentCarousel_user on User {
        serviceId
        ...ItemTilesCarousel_user
    }
`;

type CarouselContentProps = {
    user?: HpSharedVideoComponentCarousel_user$key | null;
    fetchFavorites: boolean;
    uriRef: string;
};

const CarouselContent: FC<CarouselContentProps> = ({ uriRef, fetchFavorites, user: userRef }) => {
    const environment = useRelayEnvironment();
    const { isMobile } = useHomePageContext();
    const { currency } = useCurrency();

    const data = useLazyLoadQuery<HpSharedVideoComponentCarouselQueryType>(carouselItemsQuery, {
        uriRef,
    });

    const { data: viewer, loadNext } = usePaginationFragment<
        HpSharedVideoComponentCarouselQueryType,
        HpSharedVideoComponentCarousel_viewer$key
    >(viewerFragment, data.viewer);

    const user = useFragment(userFragment, userRef ?? null);
    const items = flattenConnectionNodes(viewer?.itemSearchItems);
    const totalResults = viewer?.itemSearchItems?.totalResults || 0;
    const userId = user?.serviceId;

    if (!items.length) {
        return null;
    }

    return (
        <SharedFavoritesProvider
            viewer={viewer}
            userId={userId}
            itemIds={items.map(item => item?.serviceId)}
            fetchFavorites={fetchFavorites}
            environment={environment}
        >
            {({ favorites }: $TSFixMe) => (
                <ItemTilesCarousel
                    viewer={viewer}
                    user={user}
                    items={items}
                    currency={currency}
                    authModalLoader={authModalLoader}
                    imageBackgroundColor="white"
                    carouselCapacity={CAROUSEL_CAPACITY}
                    totalResults={totalResults}
                    loadNext={loadNext}
                    trackingList={VIDEO_MODULE_TRIGGER}
                    itemsToShow={
                        isMobile ? CAROUSEL_ITEMS_TO_SHOW_MOBILE : CAROUSEL_ITEMS_TO_SHOW_RESP
                    }
                    tileSize={isMobile ? 'small' : 'medium'}
                    isMobile={isMobile}
                    isInfinite={!isMobile}
                    showArrows={!isMobile}
                    showQuickView={false}
                    gapSize="small"
                    fullWidth={!!isMobile}
                    favoritesComponentOverrides={items.map(item => () => (
                        <SharedHeart
                            item={item}
                            isMobile={isMobile}
                            trackingList={`homepage - ${VIDEO_MODULE_TRIGGER}`}
                            favorites={favorites}
                        />
                    ))}
                    tileClassName={classnames(
                        dibsCss.bgWhite,
                        dibsCss.ptSmaller,
                        dibsCss.pxSmaller
                    )}
                />
            )}
        </SharedFavoritesProvider>
    );
};

type Props = {
    uriRef: string;
    user?: HpSharedVideoComponentCarousel_user$key | null;
    fetchFavorites: boolean;
};

const HpSharedVideoComponentCarousel: FC<Props> = ({ uriRef, fetchFavorites, user }) => {
    return (
        <Suspense fallback={null}>
            <CarouselContent uriRef={uriRef} fetchFavorites={fetchFavorites} user={user} />
        </Suspense>
    );
};

export default HpSharedVideoComponentCarousel;
